import { useEffect } from 'react';
import {
  useClearRoomSelectionOnNightsChange,
  useClearRoomSelectionOnRoomCountChange,
} from './useTripDayHotelStaySync';

// Split out of useTripDayHotelStaySync.ts (US-073C) to stay under the
// package's max-lines-per-file cap.

// Clear the selected room when the stay moves to other dates (KD-3783): the
// nights and the rooms are unchanged, so neither effect above fires, yet the
// rate and its promotions (early bird…) belong to the period and must be
// quoted again. The room key is kept so the same room is picked again at its
// new price — `useAutoSelectHotelRooms` falls back to the cheapest one if it
// is gone, via `hotelRoomFallsBackToCheapest`, instead of marking the stay
// unavailable.
export function useClearRoomSelectionOnStayDateChange({
  dayIndex,
  hotel,
  isFirstInGroup,
  roomStartDate,
  isRoomSearchEnabled,
  days,
  dayRoomSelection,
  updateDay,
}: {
  dayIndex: number;
  hotel: any;
  isFirstInGroup: boolean;
  roomStartDate?: string;
  isRoomSearchEnabled: boolean;
  days: any[];
  dayRoomSelection: any;
  updateDay: (dayIndex: number, updates: Record<string, any>) => void;
}) {
  useEffect(() => {
    if (hotel?.id && isFirstInGroup && roomStartDate && isRoomSearchEnabled) {
      const storedStayStart = days[dayIndex]?.hotelStayStart;

      if (storedStayStart === roomStartDate) return;

      if (storedStayStart !== undefined && dayRoomSelection) {
        updateDay(dayIndex, {
          hotelPricing: null,
          hotelStayStart: roomStartDate,
          selectedRoom: null,
          hotelSelectedRoomKey: dayRoomSelection.roomKey ?? null,
          hotelRoomFallsBackToCheapest: true,
        });
      } else {
        updateDay(dayIndex, { hotelStayStart: roomStartDate });
      }
    }
  }, [
    roomStartDate,
    hotel?.id,
    isFirstInGroup,
    isRoomSearchEnabled,
    dayIndex,
    updateDay,
    days,
    dayRoomSelection,
  ]);
}

// Groups the three "stale room selection" effects behind one call (keeps
// `useTripDayHotelData` under the package's max-lines-per-function cap): the
// nights changed, the room count changed, or the stay moved to other dates.
// Each guards its own condition and is a no-op when it doesn't apply.
export function useClearStaleRoomSelection({
  dayIndex,
  hotel,
  isFirstInGroup,
  nights,
  roomsComposition,
  roomStartDate,
  isRoomSearchEnabled,
  days,
  dayRoomSelection,
  updateDay,
}: {
  dayIndex: number;
  hotel: any;
  isFirstInGroup: boolean;
  nights: number;
  roomsComposition: any[];
  roomStartDate?: string;
  isRoomSearchEnabled: boolean;
  days: any[];
  dayRoomSelection: any;
  updateDay: (dayIndex: number, updates: Record<string, any>) => void;
}) {
  useClearRoomSelectionOnNightsChange({
    dayIndex,
    hotel,
    isFirstInGroup,
    nights,
    days,
    dayRoomSelection,
    updateDay,
  });

  useClearRoomSelectionOnRoomCountChange({
    dayIndex,
    hotel,
    isFirstInGroup,
    roomsComposition,
    days,
    dayRoomSelection,
    updateDay,
  });

  useClearRoomSelectionOnStayDateChange({
    dayIndex,
    hotel,
    isFirstInGroup,
    roomStartDate,
    isRoomSearchEnabled,
    days,
    dayRoomSelection,
    updateDay,
  });
}
